import React from 'react';

const TaskimoPrivacy = () => {
  const scrollToTerms = () => {
    const termsSection = document.getElementById('terms-of-service');
    if (termsSection) {
      termsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Taskimo: Family Tasks & Rewards</h1>
          <div className="flex justify-center space-x-6">
            <a href="#privacy-policy" className="text-blue-600 hover:text-blue-800 font-semibold">Privacy Policy</a>
            <span className="text-gray-400">|</span>
            <button onClick={scrollToTerms} className="text-blue-600 hover:text-blue-800 font-semibold">Terms of Service</button>
          </div>
        </div>

        {/* Privacy Policy Section */}
        <div id="privacy-policy" className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-center mb-6">Privacy Policy</h2>
          <div className="text-sm text-gray-600 mb-6">Last Updated: October 2025</div>
          
          <p className="text-gray-700 mb-6">
            This Privacy Policy explains how Taskimo: Family Tasks & Rewards ("Taskimo", "we", "our", or "us") collects, uses, and protects your information when you use our mobile application ("the App"). By using Taskimo, you agree to the practices described in this Privacy Policy.
          </p>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">1. Overview</h3>
            <p className="text-gray-700 mb-4">
              Taskimo is a family-oriented app that helps parents organize daily routines, assign responsibilities, and motivate children through rewards.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>The App is intended for parents and guardians only.</li>
              <li>Children do not create their own accounts, and they access the app only using a code provided by the parent.</li>
              <li>We do not collect or store any personally identifiable information from children.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">2. Information We Collect</h3>
            <p className="text-gray-700 mb-4">
              We strive to minimize data collection. Taskimo may collect the following limited information from parents:
            </p>
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Parent Account Information:</h4>
              <p className="text-gray-700">Email address, display name, and basic account settings (if provided during setup).</p>
            </div>
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">App Usage Data:</h4>
              <p className="text-gray-700">Anonymous analytics such as app version, device type, and general usage statistics — used solely to improve app performance and user experience.</p>
            </div>
            <p className="text-gray-700">
              We do not collect sensitive data such as location, contacts, photos, or payment information directly. All payments are handled securely through Apple's App Store under your Apple ID.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">3. Children's Privacy</h3>
            <p className="text-gray-700 mb-4">
              We are fully compliant with COPPA (Children's Online Privacy Protection Act).
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Children cannot register, enter personal details, or communicate within the app.</li>
              <li>They access a simplified view of the app using a secure family code created by their parent.</li>
            </ul>
            <p className="text-gray-700">
              We do not knowingly collect, use, or store personal data from children under 13 years of age. If we become aware that such data has been inadvertently collected, it will be permanently deleted.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">4. How We Use Information</h3>
            <p className="text-gray-700 mb-4">The limited data we collect is used only for:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Maintaining your account and enabling app functionality</li>
              <li>Improving the user experience and app performance</li>
              <li>Providing customer support if you contact us</li>
            </ul>
            <p className="text-gray-700">
              We do not sell, trade, or share your information with third parties for advertising or marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">5. Data Security</h3>
            <p className="text-gray-700">
              We take your privacy seriously. All data is transmitted securely using HTTPS encryption, and stored only as long as necessary to provide our services. We use industry-standard measures to protect your data from unauthorized access, alteration, or loss.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">6. Data Storage and Retention</h3>
            <p className="text-gray-700">
              Your minimal account data is stored securely and retained only for as long as your account remains active. When you delete your account or uninstall the app, all identifiable data is automatically removed from our systems.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">7. Subscriptions and Payments</h3>
            <p className="text-gray-700">
              All subscription payments are handled securely by Apple's App Store. We do not have access to your payment details or billing information. Please refer to Apple's privacy policies for details on payment data handling.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">8. Your Rights</h3>
            <p className="text-gray-700 mb-4">
              Depending on your location (such as the EU or UK), you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Request a copy of your stored data</li>
              <li>Request deletion or correction of your information</li>
              <li>Withdraw consent at any time by deleting the app or contacting us</li>
            </ul>
            <p className="text-gray-700">
              You can contact us for any privacy-related request at: 📩 hi@n1inovasyon.com
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">9. Third-Party Services</h3>
            <p className="text-gray-700">
              Taskimo does not use third-party advertising SDKs or tracking libraries. Anonymous analytics may be collected through Apple's system frameworks (e.g., Crashlytics, App Store Analytics) to improve stability and user experience.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">10. Changes to This Policy</h3>
            <p className="text-gray-700">
              We may update this Privacy Policy occasionally to reflect changes in our practices or for legal compliance. When we do, we will update the "Last Updated" date above. Your continued use of the App means you accept the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">11. Contact Us</h3>
            <p className="text-gray-700">
              If you have any questions, requests, or concerns about this Privacy Policy, please contact us at: 📩 hi@n1inovasyon.com
            </p>
          </section>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              ✅ This Privacy Policy meets Apple's App Store Guidelines (Section 5.1.1, 5.1.2, and 1.1.1) and is fully compliant with:
            </p>
            <ul className="list-disc pl-6 text-green-800 text-sm mt-2">
              <li>COPPA (no child account creation or data collection)</li>
              <li>GDPR (data minimization and deletion rights)</li>
              <li>Apple App Store Privacy Labels (no tracking, no third-party sharing)</li>
            </ul>
          </div>
        </div>

        {/* Terms of Service Section */}
        <div id="terms-of-service" className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center mb-6">Terms of Service</h2>
          <div className="text-sm text-gray-600 mb-6">Last updated: October 2025</div>
          
          <p className="text-gray-700 mb-6">
            Welcome to Taskimo: Family Tasks & Rewards ("the App"), operated by the Taskimo Team ("we," "our," or "us"). By downloading or using the App, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the App.
          </p>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">1. Use of the App</h3>
            <p className="text-gray-700 mb-4">
              Taskimo is designed to help parents manage family routines, assign tasks, and motivate children through rewards. The App is intended to be used by parents or guardians. Children cannot create an account or sign up on their own — they can only access the App using a secure family code generated and provided by the parent.
            </p>
            <p className="text-gray-700">
              You agree to use the App only for lawful, personal, and non-commercial purposes.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">2. Accounts and Access</h3>
            <p className="text-gray-700 mb-4">
              Certain features of the App require a parent or guardian account. Once a parent creates an account, they can share a temporary or permanent access code with their children to use the App in limited mode.
            </p>
            <p className="text-gray-700">
              Children do not create personal profiles, do not enter personal data, and do not have access to communication or data-sharing features. We do not knowingly collect or store any personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">3. Subscriptions and Payments</h3>
            <p className="text-gray-700">
              Some features of Taskimo require a paid subscription. All purchases and payments are handled securely through Apple's App Store under your Apple ID. Subscriptions automatically renew unless canceled at least 24 hours before the renewal period. You can manage or cancel your subscription at any time in your App Store account settings.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">4. Privacy</h3>
            <p className="text-gray-700">
              Your privacy is important to us. Taskimo does not collect or share any personally identifiable data from children. Please refer to our Privacy Policy for more details on how we handle parent account information and anonymized app usage data.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">5. Limitation of Liability</h3>
            <p className="text-gray-700">
              We make every effort to ensure that Taskimo operates reliably. However, we are not liable for any damages, losses, or interruptions resulting from the use or inability to use the App.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">6. Termination</h3>
            <p className="text-gray-700">
              We may suspend or terminate your access if you misuse the App or violate these Terms. You may stop using the App at any time by deleting it from your device.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">7. Updates to Terms</h3>
            <p className="text-gray-700">
              We may update these Terms periodically. Continued use of the App after an update constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">8. Contact</h3>
            <p className="text-gray-700">
              If you have any questions about these Terms, please contact us at: 📩 hi@n1inovasyon.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TaskimoPrivacy;